import * as Sentry from '@sentry/react';
import {DependencyList, useMemo} from 'react';
import {HttpClient, POST} from '~/library/network-facade';

export enum MetricsType {
    Gauge = 'gauge',
    Counter = 'counter',
    Histogram = 'histogram',
}

export async function sendMetrics(http: HttpClient, data: {name: string; type: MetricsType; value: number | string}) {
    try {
        await http('v1/stat/mobile', POST(data));
    } catch (error) {
        Sentry.captureException(error);
    }
}

export function createBeacon(http: HttpClient) {
    const beacon = {
        count(name: string, value = 1) {
            sendMetrics(http, {type: MetricsType.Counter, name, value});
        },
        gauge(name: string, value: string | number) {
            sendMetrics(http, {type: MetricsType.Gauge, name, value});
        },
        histogram(name: string, value: string | number) {
            sendMetrics(http, {type: MetricsType.Histogram, name, value});
        },
    };
    return beacon;
}

const logColors = {
    default: '#8989EF',
};

export function createLogger(processName: string, color: keyof typeof logColors | (string & {}) = logColors.default) {
    function log(message: string, _level: Sentry.SeverityLevel = 'info') {
        console.log(`%c[${processName}]`, `color:${color};`, message);
    }
    const logger = {
        info(msg) {
            log(msg);
        },
        warn(msg) {
            log(msg, 'warning');
        },
        fail(msg) {
            log(msg, 'error');
        },
    } satisfies {
        [key: string]: (message: string) => void;
    };

    return logger;
}

export function useLogger(...[name, color, deps = []]: [...Parameters<typeof createLogger>, deps?: DependencyList]) {
    return useMemo(() => createLogger(name), [name, color, ...deps]);
}
